import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Trophy, Lock, Flame, Star, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { AchievementUnlockModal } from '@/components/AchievementUnlockModal';

interface Achievement {
  id: string;
  category: string;
  title: string;
  description: string;
  icon: string;
  xp_reward: number;
  target_value: number;
  sort_order: number;
  current_progress: number;
  unlocked: boolean;
  unlocked_at: string | null;
  shareable?: boolean;
  slava_line?: string | null;
}

const categoryLabels: Record<string, string> = {
  start: '🚀 Старт',
  streak: '🔥 Стрики',
  activity: '⚡ Активность',
  progress: '📊 Прогресс',
  steps: '🚶 Шаги',
  nutrition: '🥗 Питание',
};

export default function Achievements() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  // Окно «Не сейчас» в момент достижения — не последний шанс: открытым можно
  // поделиться отсюда в любой день (05.10.2026).
  const [sharing, setSharing] = useState<Achievement | null>(null);

  useEffect(() => {
    if (!profile?.id) return;
    loadAchievements();
  }, [profile?.id]);

  async function loadAchievements() {
    setLoading(true);
    const { data } = await supabase.rpc('get_user_achievements', { uid: profile!.id });
    if (data) setAchievements(data as Achievement[]);
    setLoading(false);
  }

  if (!profile) return null;

  const xp = profile.xp ?? 0;
  const level = profile.level ?? 1;
  const xpForNext = level * level * 500;
  const xpForPrev = (level - 1) * (level - 1) * 500;
  const percent = Math.min(100, ((xp - xpForPrev) / (xpForNext - xpForPrev)) * 100);

  const unlocked = achievements.filter((a) => a.unlocked);
  const locked = achievements.filter((a) => !a.unlocked);

  const grouped = (list: Achievement[]) => {
    const map = new Map<string, Achievement[]>();
    list.forEach((a) => {
      const arr = map.get(a.category) ?? [];
      arr.push(a);
      map.set(a.category, arr);
    });
    return map;
  };

  return (
    <div className="px-5 pt-4 pb-28 max-w-md mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => nav(-1)}
          className="w-10 h-10 rounded-xl bg-bg-card border border-line flex items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <div>
          <h1 className="font-display text-xl font-bold">Достижения</h1>
          <div className="text-xs text-ink-dim">
            {unlocked.length} из {achievements.length}
          </div>
        </div>
      </div>

      {/* Уровень и XP */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-elevated border-emerald-glow/30 p-5 mb-5"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-glow to-sky-glow flex items-center justify-center">
            <Trophy size={28} className="text-bg" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-ink-dim">Текущий уровень</div>
            <div className="font-display text-2xl font-bold text-emerald-glow">
              {level}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-ink-dim">Очки опыта</div>
            <div className="font-display text-lg font-bold">{xp} XP</div>
          </div>
        </div>
        <div className="h-2 bg-line rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-glow to-sky-glow transition-all duration-700"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="text-[11px] text-ink-dim text-center mt-2">
          {xpForNext - xp} XP до уровня {level + 1}
        </div>
      </motion.div>

      {/* Стрики */}
      <div className="grid grid-cols-2 gap-2 mb-5">
        <div className="card p-3 border-warn/20">
          <div className="flex items-center gap-2 mb-1">
            <Flame size={14} className="text-warn" />
            <span className="text-xs text-ink-dim">Дневник</span>
          </div>
          <div className="font-display text-xl font-bold text-warn">
            {profile.current_streak_meals ?? 0} {(profile.current_streak_meals ?? 0) === 1 ? 'день' : 'дней'}
          </div>
          <div className="text-[10px] text-ink-dim mt-0.5">
            рекорд: {profile.longest_streak_meals ?? 0}
          </div>
        </div>
        <div className="card p-3 border-sky-glow/20">
          <div className="flex items-center gap-2 mb-1">
            <Star size={14} className="text-sky-glow" />
            <span className="text-xs text-ink-dim">Достижений</span>
          </div>
          <div className="font-display text-xl font-bold text-sky-glow">
            {unlocked.length}/{achievements.length}
          </div>
          <div className="text-[10px] text-ink-dim mt-0.5">
            {Math.round((unlocked.length / Math.max(1, achievements.length)) * 100)}% открыто
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-ink-muted py-10">Загружаем…</div>
      ) : (
        <>
          {/* Полученные */}
          {unlocked.length > 0 && (
            <div className="mb-6">
              <div className="font-display font-bold text-sm mb-3 flex items-center gap-2">
                <span>✓</span> Полученные ({unlocked.length})
              </div>
              {Array.from(grouped(unlocked).entries()).map(([cat, items]) => (
                <div key={cat} className="mb-4">
                  <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-2">
                    {categoryLabels[cat] || cat}
                  </div>
                  <div className="space-y-2">
                    {items.map((a) => (
                      <AchievementCard key={a.id} achievement={a} onShare={setSharing} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Закрытые */}
          {locked.length > 0 && (
            <div>
              <div className="font-display font-bold text-sm mb-3 flex items-center gap-2 text-ink-dim">
                <Lock size={14} /> В процессе ({locked.length})
              </div>
              {Array.from(grouped(locked).entries()).map(([cat, items]) => (
                <div key={cat} className="mb-4">
                  <div className="text-[11px] text-ink-dim uppercase tracking-wider mb-2">
                    {categoryLabels[cat] || cat}
                  </div>
                  <div className="space-y-2">
                    {items.map((a) => (
                      <AchievementCard key={a.id} achievement={a} onShare={setSharing} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <AchievementUnlockModal
        open={!!sharing}
        achievement={sharing}
        onClose={() => setSharing(null)}
        celebrate={false}
      />
    </div>
  );
}

function AchievementCard({
  achievement: a,
  onShare,
}: {
  achievement: Achievement;
  onShare: (a: Achievement) => void;
}) {
  const percent = Math.min(100, (a.current_progress / a.target_value) * 100);
  const unlocked = a.unlocked;
  // Делимся только тем, что понятно снаружи — тем же признаком, что и окно достижения.
  const canShare = unlocked && !!a.shareable;

  return (
    <div
      onClick={canShare ? () => onShare(a) : undefined}
      role={canShare ? 'button' : undefined}
      className={`card p-3 flex items-center gap-3 transition-opacity ${
        !unlocked ? 'opacity-60' : 'border-emerald-glow/30 bg-emerald-glow/5'
      } ${canShare ? 'cursor-pointer active:scale-[0.99] transition-transform' : ''}`}
    >
      <div className="text-3xl shrink-0">{a.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`font-medium text-sm ${unlocked ? '' : 'text-ink-muted'}`}>
            {a.title}
          </span>
          {unlocked && (
            <span className="text-[10px] bg-emerald-glow/15 text-emerald-glow px-1.5 py-0.5 rounded-full">
              +{a.xp_reward} XP
            </span>
          )}
        </div>
        <div className="text-[11px] text-ink-dim mb-1">{a.description}</div>
        {!unlocked && a.target_value > 1 && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 bg-line rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-glow transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="text-[10px] text-ink-dim">
              {a.current_progress}/{a.target_value}
            </div>
          </div>
        )}
      </div>
      {canShare && (
        <div className="shrink-0 w-9 h-9 rounded-xl bg-bg-elevated border border-line flex items-center justify-center text-emerald-glow" aria-label="Поделиться">
          <Share2 size={15} />
        </div>
      )}
    </div>
  );
}
